// colDef/softProgramColDef.tsx
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  SoftProgramStatus,
  SoftProgramType,
} from "@/services/softProgram.service";
import { formatDate } from "@/utils/formatDate.util";
import { createColumnHelper } from "@tanstack/react-table";
import { Pencil, Trash2 } from "lucide-react";
import { Link } from "react-router-dom";

const columnHelper = createColumnHelper<SoftProgramType>();

export type SoftProgramActionType = {
  id: string;
  name: string;
  action: "edit" | "delete";
};

// ─── Status pill config ───────────────────────────────────────────────────────

const STATUS_CONFIG: Record<
  SoftProgramStatus,
  { label: string; className: string }
> = {
  completed: {
    label: "Completed",
    className: "bg-gray-100 text-gray-600",
  },
  ongoing: {
    label: "Ongoing",
    className: "bg-gray-900 text-white",
  },
  upcoming: {
    label: "Upcoming",
    className: "bg-gray-100 text-gray-600",
  },
};

export const createSoftProgramColumns = (
  onActionClick: (payload: SoftProgramActionType) => void
) => [
  // Program Name — link
  columnHelper.accessor("name", {
    header: "Program Name",
    cell: (info) => (
      <Link
        to={`/activities/soft-skills/${info.row.original._id}`}
        className="font-medium hover:underline"
      >
        {info.getValue()}
      </Link>
    ),
  }),

  // Vendor — plain
  columnHelper.accessor("vendor", {
    header: "Vendor",
    cell: (info) => <span className="text-sm">{info.getValue()}</span>,
  }),

  // Duration — plain
  columnHelper.accessor("duration", {
    header: "Duration",
    cell: (info) => <span className="text-sm">{info.getValue()}</span>,
  }),

  // Max Participants
  columnHelper.accessor("maxParticipants", {
    header: "Max Participants",
    cell: (info) => (
      <span className="text-sm font-medium">{info.getValue()}</span>
    ),
  }),

  // Mode
  columnHelper.accessor("mode", {
    header: "Mode",
    cell: (info) => <span className="text-sm">{info.getValue()}</span>,
  }),

  // Start Date — YYYY-MM-DD
  columnHelper.accessor("startDate", {
    header: "Start Date",
    cell: (info) => (
      <span className="whitespace-nowrap text-sm">
        {formatDate(info.getValue(), { locale: "en-CA", dateStyle: "short" })}
      </span>
    ),
  }),

  // Status pill
  columnHelper.accessor("status", {
    header: "Status",
    cell: (info) => {
      const cfg = STATUS_CONFIG[info.getValue()];
      return (
        <span
          className={cn(
            "whitespace-nowrap rounded-full px-3 py-1 text-xs font-semibold",
            cfg.className
          )}
        >
          {cfg.label}
        </span>
      );
    },
  }),

  // Inline edit / delete
  columnHelper.display({
    id: "actions",
    header: "Actions",
    cell: (info) => {
      const data = info.row.original;
      return (
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={() =>
              onActionClick({ id: data._id, name: data.name, action: "edit" })
            }
            aria-label="Edit program"
          >
            <Pencil size={15} />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={() =>
              onActionClick({ id: data._id, name: data.name, action: "delete" })
            }
            aria-label="Delete program"
          >
            <Trash2 size={15} />
          </Button>
        </div>
      );
    },
  }),
];
